import type { CoolerDTO } from "seedgreen-shared/dtos/CoolerDTO";
import type { RanchSeasonDTO } from "seedgreen-shared/dtos/RanchSeasonDTO";

export interface RegionOption {
	id: number;
	name: string;
}

type RegionCooler = Pick<CoolerDTO, "id" | "regionId" | "regionName">;
type RegionRanchSeason = Pick<
	RanchSeasonDTO,
	"ranchId" | "seasonId" | "coolerId"
>;

/**
 * Distinct regions of the coolers that at least one ranch season uses, sorted by name.
 * Regions without any ranch would only ever filter down to an empty list, so they are left out.
 */
export function getRegionOptions(
	coolers: RegionCooler[],
	ranchSeasons: RegionRanchSeason[],
): RegionOption[] {
	const usedCoolerIds = new Set(ranchSeasons.map((rs) => rs.coolerId));
	const regionsById = new Map<number, RegionOption>();

	for (const cooler of coolers) {
		if (cooler.regionId === undefined || !usedCoolerIds.has(cooler.id))
			continue;
		if (!regionsById.has(cooler.regionId)) {
			regionsById.set(cooler.regionId, {
				id: cooler.regionId,
				name: cooler.regionName ?? String(cooler.regionId),
			});
		}
	}

	return [...regionsById.values()].sort((a, b) =>
		a.name.localeCompare(b.name),
	);
}

export function getRegionCoolerIds(
	coolers: RegionCooler[],
	regionId: number,
): Set<number> {
	return new Set(
		coolers
			.filter((cooler) => cooler.regionId === regionId)
			.map((cooler) => cooler.id),
	);
}

/**
 * Ranches whose ranch season uses one of the region's coolers. A ranch can move between regions
 * from one season to the next, so with a season only that season's ranch seasons count; without
 * one, a ranch is in every region it has been in.
 */
export function getRanchIdsInRegion(
	ranchSeasons: RegionRanchSeason[],
	regionCoolerIds: Set<number>,
	seasonId: number | null = null,
): Set<number> {
	const ranchIds = new Set<number>();
	for (const rs of ranchSeasons) {
		const isInSeason = seasonId === null || rs.seasonId === seasonId;
		if (isInSeason && regionCoolerIds.has(rs.coolerId)) {
			ranchIds.add(rs.ranchId);
		}
	}
	return ranchIds;
}

/**
 * Resolves the selected (possibly persisted) region against the customer's regions. While the
 * region data is loading the id is trusted, so that region-scoped requests wait instead of
 * briefly running unfiltered. Once loaded, an id the customer does not have (e.g. persisted
 * under another customer) resolves to null, meaning all regions.
 */
export function resolveSelectedRegion(
	regionId: number | null,
	options: RegionOption[],
	isLoading: boolean,
): number | null {
	if (regionId === null || isLoading) return regionId;
	return options.some((option) => option.id === regionId) ? regionId : null;
}

/**
 * Narrows a ranch selection to the ranches of the selected region for an API request.
 * The API treats an empty ranch list as "all ranches", so when none of the selected ranches are
 * in the region, every ranch in the region is requested instead.
 * @param selected - Selected ranch ids; empty means all ranches
 * @param inRegion - Ranch ids in the selected region, or null when no region is selected
 * @returns The selection unchanged without a region, otherwise ids sorted ascending so query keys stay stable
 */
export function narrowRanchIdsToRegion(
	selected: number[],
	inRegion: Set<number> | null,
): number[] {
	if (inRegion === null) return selected;

	const narrowed = selected.filter((id) => inRegion.has(id));
	return (narrowed.length > 0 ? narrowed : [...inRegion]).sort(
		(a, b) => a - b,
	);
}
